// Padrão dos dados digitados: não importa quem digita nem como, tudo sai no
// mesmo formato. Dois momentos:
//   - enquanto digita: CPF/CNPJ com pontos e traço, telefone com DDD entre
//     parênteses, placa em maiúsculas, km com ponto de milhar, só números
//     onde só cabe número;
//   - ao sair do campo (e ao salvar): nomes de pessoas e cidades com as
//     iniciais maiúsculas, valores em reais com vírgula, espaços repetidos
//     removidos, e-mail em minúsculas.
// Nomes de empresas ficam como digitados: têm siglas (TMT, W7, CAD Brasil)
// que não dá para adivinhar.
import { lerNumero, litros, numeroBR, placa as formatarPlaca } from './formato.js';
import { apenasDigitos, normalizarDocumento, normalizarPlaca } from './validacao.js';

// ---------------------------------------------------------------------
// Enquanto digita
// ---------------------------------------------------------------------
function cpfParcial(d) {
  let r = d.slice(0, 3);
  if (d.length > 3) r += `.${d.slice(3, 6)}`;
  if (d.length > 6) r += `.${d.slice(6, 9)}`;
  if (d.length > 9) r += `-${d.slice(9, 11)}`;
  return r;
}

function cnpjParcial(c) {
  let r = c.slice(0, 2);
  if (c.length > 2) r += `.${c.slice(2, 5)}`;
  if (c.length > 5) r += `.${c.slice(5, 8)}`;
  if (c.length > 8) r += `/${c.slice(8, 12)}`;
  if (c.length > 12) r += `-${c.slice(12, 14)}`;
  return r;
}

// 52998224725 → 529.982.247-25
export function mascaraCpf(texto) {
  return cpfParcial(apenasDigitos(texto).slice(0, 11));
}

// CPF até 11 números; com o 12º caractere ou com letra, vira CNPJ
// (inclusive o novo, com letras: 12.ABC.345/01DE-35).
export function mascaraDocumento(texto) {
  const c = normalizarDocumento(texto).slice(0, 14);
  if (/^\d*$/.test(c) && c.length <= 11) return cpfParcial(c);
  return cnpjParcial(c);
}

// 48999990000 → (48) 99999-0000 · 4833330000 → (48) 3333-0000
// Aceita +55 na frente e o zero de discagem (048…).
export function mascaraTelefone(texto) {
  let d = apenasDigitos(texto);
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  d = d.replace(/^0+/, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  if (resto.length <= 4) return `(${ddd}) ${resto}`;
  if (d.length <= 10) return `(${ddd}) ${resto.slice(0, 4)}-${resto.slice(4)}`;
  return `(${ddd}) ${resto.slice(0, 5)}-${resto.slice(5)}`;
}

// Maiúsculas, sem espaço; a placa antiga ganha o hífen (ABC-1234) assim que
// dá para saber que ela é antiga.
export function mascaraPlaca(texto) {
  const p = normalizarPlaca(texto).slice(0, 7);
  if (/^[A-Z]{3}\d{2}/.test(p)) return `${p.slice(0, 3)}-${p.slice(3)}`;
  return p;
}

// 790223 → 790.223
export function mascaraMilhar(texto, maximo = 8) {
  const d = apenasDigitos(texto).replace(/^0+(?=\d)/, '').slice(0, maximo);
  return d.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function mascaraDigitos(texto, maximo) {
  return apenasDigitos(texto).slice(0, maximo);
}

// ---------------------------------------------------------------------
// Ao sair do campo e ao salvar
// ---------------------------------------------------------------------
export function textoLimpo(texto) {
  return String(texto ?? '').replace(/\s+/g, ' ').trim();
}

export function emailLimpo(texto) {
  return String(texto ?? '').replace(/\s+/g, '').toLowerCase();
}

const PARTICULAS = new Set(['da', 'das', 'de', 'do', 'dos', 'e', 'di', 'du', 'del', 'della', 'van', 'von']);
const ROMANOS = /^(ii|iii|iv|vi|vii|viii|ix)$/i;

// "JOÃO JOSÉ DOS SANTOS" / "joão josé dos santos" → "João José dos Santos"
export function nomeProprio(texto) {
  const limpo = textoLimpo(texto);
  if (!limpo) return '';
  return limpo
    .split(' ')
    .map((palavra, i) => {
      const minuscula = palavra.toLocaleLowerCase('pt-BR');
      if (i > 0 && PARTICULAS.has(minuscula)) return minuscula;
      if (i > 0 && ROMANOS.test(palavra)) return palavra.toUpperCase();
      return minuscula.replace(/(^|[-'’.])(\p{L})/gu, (_, sep, letra) => sep + letra.toLocaleUpperCase('pt-BR'));
    })
    .join(' ');
}

// Marcas conhecidas saem sempre com a mesma grafia; as outras, como digitadas.
const MARCAS = new Map(
  [
    'Scania', 'Volvo', 'Mercedes-Benz', 'Volkswagen', 'VW', 'DAF', 'Iveco', 'Ford', 'International', 'MAN',
    'Sinotruk', 'Shacman', 'Foton', 'JAC', 'Hyundai', 'Agrale', 'Randon', 'Librelato', 'Facchini', 'Guerra',
    'Noma', 'Rodofort', 'Pastre', 'Schiffer', 'Rossetti', 'Rodotécnica', 'Recrusul', 'Truckvan', 'Krone',
  ].map((m) => [m.toLocaleLowerCase('pt-BR'), m]),
);
MARCAS.set('mercedes', 'Mercedes-Benz');
MARCAS.set('mercedes benz', 'Mercedes-Benz');
MARCAS.set('mb', 'Mercedes-Benz');
MARCAS.set('rodotecnica', 'Rodotécnica');

export function marca(texto) {
  const limpo = textoLimpo(texto);
  return MARCAS.get(limpo.toLocaleLowerCase('pt-BR')) ?? limpo;
}

// "1234,5" → "1.234,50" · "150" → "150,00". Se não for número, deixa como está
// para a validação avisar.
export function dinheiroDigitado(texto) {
  const n = lerNumero(texto);
  if (n === null || Number.isNaN(n)) return textoLimpo(texto);
  return numeroBR(n, { casas: 2 });
}

// Litros: "379" → "379" · "379.5" → "379,5" · "40,123" → "40,123"
export function litrosDigitado(texto) {
  const n = lerNumero(texto);
  if (n === null || Number.isNaN(n)) return textoLimpo(texto);
  return litros(n);
}

// "13.5" → "13,5" · "13" → "13"
export function percentualDigitado(texto) {
  const n = lerNumero(texto);
  if (n === null || Number.isNaN(n)) return textoLimpo(texto);
  return numeroBR(n);
}

// ---------------------------------------------------------------------
// Formatos dos campos: `digitando` roda a cada tecla; `final`, ao sair do
// campo e ao ler o formulário para salvar.
// ---------------------------------------------------------------------
export const FORMATOS = {
  cpf: { digitando: mascaraCpf },
  documento: { digitando: mascaraDocumento },
  telefone: { digitando: mascaraTelefone },
  placa: { digitando: mascaraPlaca, final: (t) => formatarPlaca(t) },
  km: { digitando: (t) => mascaraMilhar(t, 8) },
  ano: { digitando: (t) => mascaraDigitos(t, 4) },
  renavam: { digitando: (t) => mascaraDigitos(t, 11) },
  dias: { digitando: (t) => mascaraDigitos(t, 3) },
  dia: { digitando: (t) => mascaraDigitos(t, 2) },
  litros: { final: litrosDigitado },
  rntrc: { digitando: (t) => mascaraDigitos(t, 12) },
  dinheiro: { final: dinheiroDigitado },
  percentual: { final: percentualDigitado },
  nome: { final: nomeProprio },
  marca: { final: marca },
  email: { final: emailLimpo },
  texto: { final: textoLimpo },
};

// Formato de quem não escolheu um: e-mail, telefone ou texto de uma linha.
export function formatoPadrao(tipo) {
  if (tipo === 'email') return 'email';
  if (tipo === 'tel') return 'telefone';
  if (tipo === 'text') return 'texto';
  return null;
}
